// src/components/Header.jsx
import { Link, NavLink } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Container from "./Container";

const mainNav = [
  // About handled separately (dropdown)
  { to: "/testing-locator", label: "Testing Locator" },
  { to: "/quiz", label: "Quiz" },
  { to: "/resources", label: "Resources" },
  { to: "/support", label: "Support" },
  { to: "/contact", label: "Contact" },
  { to: "/blood-donation", label: "Blood Donation" },
];

const aboutNav = [
  { to: "/about", label: "Overview" },
  { to: "/about/genetics", label: "Genetics & Family Journey" },
  { to: "/about/care", label: "Care & Treatment" },
  { to: "/about/symptoms", label: "Symptoms" },
  { to: "/about/prevention", label: "Prevention & Testing" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);   // mobile accordion
  const [aboutMenuOpen, setAboutMenuOpen] = useState(false); // desktop dropdown
  const aboutRef = useRef(null);

  // Close desktop About menu on outside click / Esc
  useEffect(() => {
    function onDocClick(e) {
      if (!aboutRef.current) return;
      if (!aboutRef.current.contains(e.target)) setAboutMenuOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setAboutMenuOpen(false);
    }
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b">
      <Container className="h-16 flex items-center justify-between">
        {/* Logo + brand */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/logo.png"
            alt="Know Thalassemia logo"
            className="w-40 h-auto"
            width="400"
            height="200"
            decoding="async"
            fetchpriority="high"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-5">
          {/* About dropdown (no link, tap only) */}
          <div ref={aboutRef} className="relative">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={aboutMenuOpen}
              className={`text-sm transition-colors px-1 py-1 rounded
                ${aboutMenuOpen ? "text-rose-600 font-medium" : "text-slate-700 hover:text-rose-600"}`}
              onClick={() => setAboutMenuOpen((v) => !v)} // tap/click to toggle
            >
              About <span className="ml-1 inline-block align-middle">▾</span>
            </button>

            {aboutMenuOpen && (
              <div
                role="menu"
                className="absolute left-0 mt-2 w-64 rounded-xl border bg-white shadow-lg p-2"
              >
                {aboutNav.map((x) => (
                  <NavLink
                    key={x.to}
                    to={x.to}
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded text-sm ${
                        isActive ? "bg-rose-50 text-rose-700" : "text-slate-700 hover:bg-slate-50"
                      }`
                    }
                    onClick={() => setAboutMenuOpen(false)}
                    role="menuitem"
                  >
                    {x.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {mainNav.map((x) => (
            <NavLink
              key={x.to}
              to={x.to}
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive ? "text-rose-600 font-medium" : "text-slate-700 hover:text-rose-600"
                }`
              }
            >
              {x.label}
            </NavLink>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded hover:bg-slate-100"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
        >
          <div className="space-y-1">
            <div className="h-0.5 w-5 bg-slate-700" />
            <div className="h-0.5 w-5 bg-slate-700" />
            <div className="h-0.5 w-5 bg-slate-700" />
          </div>
        </button>
      </Container>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t">
          <Container className="py-3 flex flex-col">
            {/* About accordion */}
            <button
              onClick={() => setAboutOpen((v) => !v)}
              className="py-2 text-sm flex items-center justify-between"
              aria-expanded={aboutOpen}
              aria-controls="about-accordion"
              type="button"
            >
              <span className="font-medium text-slate-800">About</span>
              <span className={`transform transition ${aboutOpen ? "rotate-180" : ""}`}>▾</span>
            </button>
            {aboutOpen && (
              <div id="about-accordion" className="pl-3">
                {aboutNav.map((x) => (
                  <NavLink
                    key={x.to}
                    to={x.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-2 text-sm ${isActive ? "text-rose-600 font-medium" : "text-slate-700"}`
                    }
                  >
                    {x.label}
                  </NavLink>
                ))}
              </div>
            )}

            {mainNav.map((x) => (
              <NavLink
                key={x.to}
                to={x.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 text-sm ${isActive ? "text-rose-600 font-medium" : "text-slate-700"}`
                }
              >
                {x.label}
              </NavLink>
            ))}
          </Container>
        </div>
      )}
    </header>
  );
}
